import React from 'react';
import { Route } from 'react-router';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import ErrorPage from '../ErrorPage/ErrorPage';
import RosterContainer from '../../containers/RosterContainer/RosterContainer';
import EventContainer from '../../containers/EventContainer';
import EventsSearchContainer from '../../containers/EventsSearchContainer';
import { NavigationVariants, ANIMATIONSTATES as ANIMATIONS } from '../../libs/animations';
import { WidgetPageWrapperWithRouteProps } from '../../libs/widgetUtilities';
import FeatureFlagWrapper from '../FeatureFlagWrapper';
import WidgetPageContainerAnimation from './WidgetStyle.style';
import EventsContainer from '../../containers/EventsContainer';
import ResetNestedRoutes from '../ResetNestedRoutes';
import NavigationHistory from '../../libs/NavigationHistory';
import TimesheetsContainer from '../../containers/TimesheetsContainer';
import TimesheetContainer from '../../containers/TimesheetContainer';

class WidgetPage extends React.Component {
  static shouldAnimate(props) {
    const { navigationHistory } = props;
    const currentURL = props.history.location.pathname;
    const { previousURL } = navigationHistory;
    /* Do not animate when landing on a widgetpage directly or when navigating in a widget itself
      (or directly between widgets)
       Other animations to handle here:
        - from widgetpage -> homepage
        - from widget B -> widget A (going back)
        - from widget A -> widet B (going "forward")
        - from popoverpage -> widgetpage
     */
    if (!navigationHistory.hasNavigated()) {
      return ANIMATIONS.firstRender;
    }
    if (navigationHistory.isHomepageURL(currentURL)) {
      if (previousURL && navigationHistory.isWidgetURL(previousURL.pathname))
        return ANIMATIONS.toHome;
      return ANIMATIONS.fromHome;
    }
    if (navigationHistory.isNavigatingBetweenWidgets()) {
      if (navigationHistory.isNavigatingInSameWidget()) return ANIMATIONS.inWidget;
      return ANIMATIONS.betweenWidgets;
    }
    if (
      previousURL &&
      navigationHistory.isHomepageURL(previousURL.pathname) &&
      navigationHistory.isWidgetURL(currentURL)
    ) {
      return ANIMATIONS.fromHome;
    }
    if (
      previousURL &&
      navigationHistory.isPopoverURL(previousURL.pathname) &&
      navigationHistory.isWidgetURL(currentURL)
    )
      return ANIMATIONS.firstRender;
    if (
      previousURL &&
      navigationHistory.isPopoverURL(currentURL) &&
      navigationHistory.isWidgetURL(previousURL.pathname)
    )
      return ANIMATIONS.toPopover;
    return false;
  }

  static getDerivedStateFromProps(props, state) {
    return {
      animation: WidgetPage.shouldAnimate(props)
    };
  }

  constructor(props) {
    super(props);
    this.widgets = {};
    this.state = { animation: WidgetPage.shouldAnimate(props), headerHeight: 0 };
    this.renderErrorPage = this.renderErrorPage.bind(this);
    this.widgetAnimationWrapperRef = React.createRef();
    this.timeoutResetTranform = null;
  }

  componentDidMount() {
    const { headerRef } = this.props;
    if (headerRef && headerRef.current) {
      this.setState({
        headerHeight: headerRef.current.getHeaderHeight()
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { navigationHistory } = this.props;
    return (
      navigationHistory.getAppNameForPath(nextProps.location.pathname) !==
        navigationHistory.getPreviousWidgetName() ||
      this.props.sessionStorageUpdateCounter !== nextProps.sessionStorageUpdateCounter ||
      this.props.cachingResultsUpdateCounter !== nextProps.cachingResultsUpdateCounter ||
      this.props.eventsFilterOptions !== nextProps.eventsFilterOptions ||
      !isEqual(this.props.user, nextProps.user)
    );
  }

  getAppNameForPath(linkPath) {
    const navigationItem = this.props.navs.find((nav) =>
      linkPath.startsWith(nav.linkTo.pathname)
    );
    return navigationItem ? i18n(navigationItem.nameMessage) : '';
  }

  renderErrorPage() {
    const { navigationHistory, router, location } = this.props;
    return (
      <WidgetPageContainerAnimation
        key={navigationHistory.getAppNameForPath(location.pathname)}
        initial="invisible"
        exit="invisible"
        animate="visibile"
        variants={NavigationVariants}
      >
        <ErrorBoundary>
          <ErrorPage
            match={router.match}
            errorCode={404}
            navigationHistory={this.props.navigationHistory}
          />
        </ErrorBoundary>
      </WidgetPageContainerAnimation>
    );
  }

  render() {
    const { routeUrlFor, location, isFeatureActive } = this.props;
    const propsForWrapper = {
      ...this.state,
      timeoutResetTranform: this.timeoutResetTranform,
      setTimeoutResetTranform: (fn) => {
        this.timeoutResetTranform = fn;
      },
      widgetAnimationWrapper: this.widgetAnimationWrapper,
      widgetAnimationWrapperRef: this.widgetAnimationWrapperRef,
      ...this.props
    };
    return (
      <ResetNestedRoutes
        location={location}
        key={location.pathname}
        keyForNestedRoutes={location.pathname}
      >
        <Route
          path={routeUrlFor('/roster/*')}
          element={
            <WidgetPageWrapperWithRouteProps {...propsForWrapper} widgetPath="/roster">
              <div className="js-widget-body">
                <div style={{ paddingTop: '48px' }} />
                <ErrorBoundary>
                  <RosterContainer {...this.props} />
                </ErrorBoundary>
              </div>
            </WidgetPageWrapperWithRouteProps>
          }
        />
        <Route
          path={routeUrlFor('/events/*')}
          element={
            <WidgetPageWrapperWithRouteProps {...propsForWrapper} widgetPath="/events">
              <ErrorBoundary>
                <EventsContainer {...this.props} ref={this.props.headerRef} />
              </ErrorBoundary>
            </WidgetPageWrapperWithRouteProps>
          }
        />
        <Route
          path={routeUrlFor('/timesheets/*')}
          element={
            <WidgetPageWrapperWithRouteProps
              {...propsForWrapper}
              widgetPath="/timesheets"
            >
              <ErrorBoundary>
                <TimesheetsContainer {...this.props} ref={this.props.headerRef} />
              </ErrorBoundary>
            </WidgetPageWrapperWithRouteProps>
          }
        />
        <Route
          path={routeUrlFor('/timesheet/:dateString')}
          element={
            <WidgetPageWrapperWithRouteProps {...propsForWrapper} widgetPath="/timesheet">
              <ErrorBoundary>
                <TimesheetContainer
                  widgetAnimationWrapperRef={this.widgetAnimationWrapperRef}
                  {...this.props}
                />
              </ErrorBoundary>
            </WidgetPageWrapperWithRouteProps>
          }
        />
        <Route
          exact
          path={routeUrlFor('/search/*')}
          element={
            <WidgetPageWrapperWithRouteProps {...propsForWrapper} widgetPath="/search">
              <FeatureFlagWrapper
                feature="eventsSearch"
                renderNotActive={() => this.renderErrorPage()}
                isFeatureActive={isFeatureActive}
              >
                <ErrorBoundary>
                  <EventsSearchContainer {...this.props} ref={this.props.headerRef} />
                </ErrorBoundary>
              </FeatureFlagWrapper>
            </WidgetPageWrapperWithRouteProps>
          }
        />
        {[routeUrlFor('/event/:id#:tab'), routeUrlFor('/event/:id')].map((path) => (
          <Route
            exact
            path={path}
            key={path}
            element={
              <WidgetPageWrapperWithRouteProps {...propsForWrapper} widgetPath="/event">
                <ErrorBoundary>
                  <EventContainer
                    widgetAnimationWrapperRef={this.widgetAnimationWrapperRef}
                    {...this.props}
                  />
                </ErrorBoundary>
              </WidgetPageWrapperWithRouteProps>
            }
          />
        ))}
        {/* Default: 404 page */}
        <Route render={this.renderErrorPage} />
      </ResetNestedRoutes>
    );
  }
}

WidgetPage.propTypes = {
  navs: PropTypes.arrayOf(
    PropTypes.shape({
      nameMessage: PropTypes.string,
      linkTo: PropTypes.string
    })
  ),
  showNotificationCenter: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string
  }).isRequired,
  routeUrlFor: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  match: PropTypes.shape({}).isRequired,
  isFeatureActive: PropTypes.func.isRequired
};

WidgetPage.defaultProps = {
  navs: []
};

function ForwardRef(props, ref) {
  return <WidgetPage {...props} headerRef={ref} />;
}

export default React.forwardRef(ForwardRef);
